import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Pencil, Scale } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ordersAPI } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import api from '@/lib/api';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standalone, read-only modal that fetches a manual reception by id.
 *
 * Props:
 *   receptionId: string | null
 *   onClose: () => void
 */
export default function ManualReceptionDetailDialog({ receptionId, onClose }) {
    const [reception, setReception] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!receptionId) {
            setReception(null);
            return;
        }
        setLoading(true);
        ordersAPI.getManualReception(receptionId)
            .then(res => setReception(res.data))
            .catch(() => setReception(null))
            .finally(() => setLoading(false));
    }, [receptionId]);

    const goToEdit = () => {
        if (!receptionId) return;
        navigate(`/orders?tab=manual&openReception=${receptionId}`);
        onClose();
    };

    const correctWeight = async (index) => {
        if (!receptionId) return;
        try {
            const res = await api.post(
                `${API_BASE}/manual-receptions/${receptionId}/correct-weight`,
                null,
                { params: { item_index: index } }
            );
            toast.success(`Ligne corrigée : ${res.data.quantity} kg × ${res.data.unit_price} €`);
            // refetch
            const fresh = await ordersAPI.getManualReception(receptionId);
            setReception(fresh.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de correction');
        }
    };

    return (
        <Dialog open={!!receptionId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="sm:max-w-6xl max-h-[90vh] flex flex-col gap-2"
                data-testid="mr-standalone-dialog"
            >
                {loading ? (
                    <div className="py-12 flex items-center justify-center text-slate-400 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement de la réception…
                    </div>
                ) : reception ? (
                    <>
                        <div className="flex-shrink-0 flex items-center gap-4 pb-2 border-b flex-wrap">
                            <h2 className="text-lg font-semibold whitespace-nowrap">
                                {reception.reception_number}
                            </h2>
                            <div className="flex items-center gap-4 text-xs flex-1 min-w-0 flex-wrap">
                                <span className="text-slate-500">{reception.supplier_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{reception.site_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{formatDate(reception.reception_date || reception.created_at)}</span>
                                {reception.document_number && (
                                    <>
                                        <span className="text-slate-400">|</span>
                                        <span className="text-slate-500">{reception.document_number}</span>
                                    </>
                                )}
                                <span className="text-slate-400">|</span>
                                <span className="font-bold text-emerald-600">{formatCurrency(reception.total_amount)}</span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto min-h-0 py-2">
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left p-3">Produit</th>
                                            <th className="text-right p-3">Prix unit.</th>
                                            <th className="text-right p-3">Qté</th>
                                            <th className="text-right p-3">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reception.items?.map((item, index) => {
                                            const showCorrection = !!reception.supplier_is_carrefour
                                                && !!item.variable_weight
                                                && Number(item.quantity) === 1
                                                && Number(item.total) > 1.5;
                                            return (
                                            <tr key={index} className="border-t">
                                                <td className="p-3">
                                                    {item.supplier_ref && <span className="font-mono text-xs text-slate-400 mr-2">{item.supplier_ref}</span>}
                                                    <span>{item.supplier_product_name || item.industrial_product_name || 'Produit'}</span>
                                                    {showCorrection && (
                                                        <button
                                                            type="button"
                                                            onClick={() => correctWeight(index)}
                                                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 text-[10px] font-semibold"
                                                            title="Recalculer poids depuis l'historique de prix"
                                                        >
                                                            <Scale className="w-3 h-3" />
                                                            Corriger pesée
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                                                <td className="p-3 text-right">{item.quantity}</td>
                                                <td className="p-3 text-right font-medium">{formatCurrency(item.total)}</td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-emerald-50">
                                        <tr>
                                            <td colSpan="3" className="p-3 text-right font-semibold">Total :</td>
                                            <td className="p-3 text-right font-bold text-emerald-700">{formatCurrency(reception.total_amount)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {reception.notes && (
                                <div className="mt-3">
                                    <p className="text-slate-500 text-sm">Notes</p>
                                    <p>{reception.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={onClose} data-testid="mr-standalone-close-btn">
                                Fermer
                            </Button>
                            <Button
                                onClick={goToEdit}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid="mr-standalone-edit-btn"
                            >
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="py-12 text-center text-slate-400 text-sm">
                        Réception introuvable.
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
